<div class="app-container">
  <div class="main-layout">
    <div class="search-panel">
      <div class="search-form-container">
        <sn-form
          class="search-form"
          [form]="form"
          [fields]="formFields"
          [model]="formModel"
        ></sn-form>
      </div>
      <footer class="footer">
        <sn-button [disabled]="isLoading" (click)="clearFilters()">{{ 'common.clearFilters' | t }}</sn-button>
        <sn-flat-button
          color="accent"
          class="searchButton"
          (click)="setAutoZoomFlag(); search()"
          [disabled]="form.invalid || isLoading"
        >
          {{ 'common.search' | t }}
        </sn-flat-button>
      </footer>
    </div>

    <div #contentArea class="content-area" [class.drawer-open]="isDrawerOpen">
      <div class="{{ sourceAndDeviceManagementEnabled ? 'alprResultsHeaderSdm' : 'alprResultsHeader' }}">
        <ng-container *ngIf="shouldShowHeaderPlaceholder; else facetsLoaded">
          <div class="headerTitle">
            <ng-container *ngIf="!isLoading; else searching">
              <span id="default">{{ 'alpr.addReasonPlaceholder' | t }}</span>
            </ng-container>
            <ng-template #searching>
              <span id="searching">{{ 'alpr.searching' | t }}</span>
            </ng-template>
          </div>
        </ng-container>
        <ng-template #facetsLoaded>
          <ng-container *ngIf="isReviewingPlates">
            <div class="facets">
              <div class="provider">
                <button mat-stroked-button class="active" data-testid="marked-for-review-label">
                  {{ 'alpr.markedForReview' | t }} ({{ reviewResults.length | fsNumber }})
                </button>
                <button mat-stroked-button (click)="backToAllResults()" data-testid="back-to-search-button">{{ 'alpr.backToSearch' | t }}</button>
              </div>
            </div>
          </ng-container>
          <ng-container *ngIf="shouldShowSdmSourceSelect">
            <fs-lpr-source-select
              [providers]="providers"
              [isDisabled]="isSdmDeepLinkActive"
              (providerSelectionChange)="onProviderSelectionChange($event)"
              (cameraModelSelectionChange)="onCameraModelSelectionChange($event)"
              (partialMatchesDisplayChange)="onPartialMatchesDisplayChange($event)"
            ></fs-lpr-source-select>
          </ng-container>
          <ng-container *ngIf="shouldShowProviderButtons">
            <div #facets class="facets">
              <div class="provider">
                <button
                  mat-stroked-button
                  [class.active]="!activeProvider"
                  (click)="providerChange(null)"
                  data-testid="provider-filter-all"
                >
                  {{ 'alpr.allResults' | t }}
                </button>
              </div>
              <div class="separator"></div>
              <div *ngFor="let provider of providers" class="provider">
                <!-- Show normal providers that are not 'Shared', or is 'Shared' but includeSharedData has to be true -->
                <button
                  *ngIf="provider.name !== 'Shared' || formModel.includeSharedData"
                  mat-stroked-button
                  (click)="providerChange(provider)"
                  [class.active]="activeProvider?.name === provider.name"
                  [attr.data-testid]="'provider-filter-' + provider.name.toLowerCase().split(' ').join('-')"
                >
                  <span
                    [ngClass]="{
                      name: provider.name !== 'Flock',
                      flockName: provider.name === 'Flock'
                    }"
                  >
                    {{ provider.name === 'Fusus' ? 'Other' : provider.name }}
                  </span>

                  <span class="count">
                    <span *ngIf="!provider.isLoading">
                      ({{ provider.total
                      }}{{ provider.warnings?.length > 0 && provider.total > 0 ? '*' : '' }})
                    </span>
                  </span>

                  <span class="loading" *ngIf="provider.isLoading">
                    <mat-spinner color="primary" diameter="20" mode="indeterminate"></mat-spinner>
                  </span>

                  <span
                    class="warning"
                    *ngIf="provider.errors?.length > 0 || provider.warnings?.length > 0"
                  >
                    <sn-icon
                      fontSet="fa-solid"
                      fontIcon="fa-triangle-exclamation"
                      [class.error-icon]="provider.errors && provider.errors.length > 0"
                      [class.warning-icon]="!(provider.errors && provider.errors.length > 0)"
                      [matTooltip]="getTotalProblems(provider) + ' ' + ('alpr.problemsWithSearch' | t)"
                    ></sn-icon>
                  </span>
                </button>
              </div>
            </div>
          </ng-container>
        </ng-template>
        <div class="actionButtons">
          <mat-button-toggle-group class="actionBtns1">
            <mat-button-toggle
              [ngStyle]="{ 'background-color': mode === 'cards' ? '#2c2a30' : '#141217' }"
              [matTooltip]="'alpr.cardView' | t"
              [attr.aria-label]="'alpr.cardView' | t"
              (click)="this.toggleMode('cards')"
            >
              <div>
                <img src="/assets/fusus-icons/SVG/grid3x3.svg" />
              </div>
            </mat-button-toggle>
            <mat-button-toggle
              [matTooltip]="'alpr.listView' | t"
              [attr.aria-label]="'alpr.listView' | t"
              (click)="this.toggleMode('list')"
            >
              <img src="/assets/fusus-icons/SVG/ic_list.svg" />
            </mat-button-toggle>
          </mat-button-toggle-group>

          <div class="actionBtns2">
            <button mat-button (click)="showMap()">
              <span>{{ mapButtonText | t }}</span>
              <sn-icon fontIcon="fa-location-dot" fontSet="fas"> </sn-icon>
            </button>
            <div class="button-divider" *ngIf="lprCsvExportEnabled"></div>
            <button 
              mat-button 
              *ngIf="lprCsvExportEnabled"
              class="csvExportButton"
              (click)="openCsvExportDialog()" 
              [disabled]="!exportButtonEnabled"
              matTooltip="{{ 'alpr.export.csvExportButton.tooltip' | t }}"
            >
              <span>{{ 'alpr.export.csvExportButton.text' | t }}</span>
              <sn-icon fontIcon="fa-arrow-down-to-line" fontSet="fas"> </sn-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="content">
        <!-- Banner message moved to lpr-view-stages component -->
        <fs-lpr-view-stages
          id="lprViewStages"
          [selectedPlate]="selectedPlate"
          [results]="results"
          [mode]="mode"
          [totalResultsCount]="totalResultsCount"
          [isNextPageDisabled]="isNextPageDisabled"
          [isLoading]="isLoading"
          [hasLoaded]="hasLoaded"
          [hasBoundingBoxParams]="!isReviewingPlates && hasBoundingBoxParams"
          (plateClicked)="onPlateClick($event)"
          [page]="page"
          [errors]="errors"
          [warnings]="warnings"
          [plate]="selectedPlate"
          [closedPlateDetails$]="closedPlateDetails$"
          [getPlateWarningInfoTooltip]="getPlateWarningInfo.bind(this)"
          [activeProvider]="activeProvider"
          [activeProviders]="activeProviders"
          [shouldShowPartialMatches]="shouldShowPartialMatches"
          [hasProviderWarnings]="hasProviderWarnings()"
          [areAllProvidersEmpty]="areAllProvidersEmpty()"
          [isReviewingPlates]="isReviewingPlates"
          (firstPageClicked)="firstPage()"
          (previousPageClicked)="previousPage()"
          (nextPageClicked)="nextPage()"
          (pageLimitChange)="pageLimitChange($event)"
          (cancelSearch)="cancelSearch()"
          (hideDetails)="onHideDetails($event)"
        >
        </fs-lpr-view-stages>
      </div>

      <div
        #resultsWrapper
        class="results-wrapper"
        [class.resizing]="isResizing"
        [class.open]="isDrawerOpen"
        [class.details-open]="selectedPlate !== null"
        mwlResizable
        [resizeEdges]="{ left: selectedPlate === null }"
        [enableGhostResize]="true"
        (resizeEnd)="onResizeEnd($event)"
      >
        <fs-fleet-query-plate-card-details
          *ngIf="selectedPlate !== null"
          data-testid="lpr-read-details-drawer"
          [plate]="selectedPlate"
          [hideDetails]="hideDetails"
          [searchReason]="formModel.searchReason"
          (searchPlate)="onSearchSpecificPlate($event)"
          (isLightboxClosed)="closeDetailsPanel($event)"
        >
        </fs-fleet-query-plate-card-details>

        <div
          class="tab-container"
          [class.isDetailsView]="selectedPlate !== null"
          [class.isMapView]="selectedPlate === null"
        >
          <mat-tab-group (selectedTabChange)="onTabChange($event)">
            <mat-tab [label]="MAP_TAB_LABEL | t">
              <div class="map-tab-content">
                <fs-plate-map
                  id="lprMapContainer"
                  (plateSelected)="onPlateClick($event.selectedPlate, $event.isHistoricalPlate)"
                  (mapSearchTriggered)="onMapSearchTriggered()"
                  [results]="isHistoricalView ? historicalResults : results"
                  [selectedPlate]="selectedPlate"
                  [isHistoricalView]="isHistoricalView"
                  [isLoadingHistoricalResults]="isLoadingHistoricalResults"
                  [didQuerySearch]="didQuerySearch"
                  [drawingTools]="true"
                  [hideDrawings]="selectedPlate !== null"
                  [disablePolygon]="true"
                  [disableCircle]="true"
                  [disablePen]="true"
                  [querySearchParams]="true"
                  [hideCameraMarkers]="true"
                  [hasPerformedValidSearch]="didQuerySearch && form.valid"
                >
                </fs-plate-map>
              </div>
            </mat-tab>

            <mat-tab [label]="READ_LIST_TAB_LABEL | t" *ngIf="selectedPlate !== null">
              <div class="list-tab-content">
                <mat-progress-bar
                  *ngIf="isLoadingHistoricalResults || pendingGeocodeRequests > 0"
                  mode="indeterminate"
                  color="accent"
                ></mat-progress-bar>
                <table mat-table [dataSource]="tableResults" class="read-list-table">
                  <ng-container matColumnDef="readTime">
                    <th mat-header-cell *matHeaderCellDef>{{ 'alpr.readTime' | t }}</th>
                    <td mat-cell *matCellDef="let element">
                      {{ element.createdTimestamp | date : 'HH:mm:ss' }}
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="thumbnail">
                    <th mat-header-cell *matHeaderCellDef>{{ 'alpr.thumbnail' | t }}</th>
                    <td mat-cell *matCellDef="let element">
                      <ng-container
                        *ngIf="thumbnailImages.length > -1 || element.contextImageUrl; else noImage"
                      >
                        <img
                          [src]="this.findThumbnailImage(element) || element.contextImageUrl"
                          (error)="$event.target.parentNode.innerHTML = '&mdash;'"
                          class="plate-thumbnail"
                        />
                      </ng-container>
                      <ng-template #noImage>
                        <span class="no-image">&mdash;</span>
                      </ng-template>
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="location">
                    <th mat-header-cell *matHeaderCellDef>{{ 'common.location' | t }}</th>
                    <td mat-cell *matCellDef="let element">
                      <div
                        *ngIf="element.geocodedAddress; else noLocation"
                        class="location-address"
                      >
                        <div class="address-line">
                          {{ element.geocodedAddress.address || ('alpr.addressUnavailable' | t) }}
                        </div>
                        <div
                          class="address-city-state"
                          *ngIf="
                            element.geocodedAddress.administrativeAreaLevel2 ||
                            element.geocodedAddress.administrativeAreaLevel1
                          "
                        >
                          {{ element.geocodedAddress.administrativeAreaLevel2 }},
                          {{ element.geocodedAddress.administrativeAreaLevel1 }}
                        </div>
                      </div>
                      <ng-template #noLocation> &mdash; </ng-template>
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="deviceName">
                    <th mat-header-cell *matHeaderCellDef>{{ 'alpr.deviceName' | t }}</th>
                    <td mat-cell *matCellDef="let element">
                      {{ element.cameraName || '&mdash;' }}
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="dateHeader">
                    <td colspan="4" mat-cell *matCellDef="let group">
                      {{ group.date }}
                    </td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                  <tr
                    mat-row
                    *matRowDef="let row; columns: displayedColumns"
                    class="historical-result-row"
                    [class.selected-row]="row.id === this.selectedPlate.id"
                    (click)="this.onPlateClick(row, true)"
                  ></tr>
                  <tr
                    mat-row
                    *matRowDef="let row; columns: ['dateHeader']; when: isDateHeader"
                    class="date-header-row"
                  ></tr>
                </table>
              </div>
            </mat-tab>

            <mat-tab *ngIf="selectedPlate !== null && selectedPlate.isHit">
              <ng-template mat-tab-label>
                {{ HITS_TAB_LABEL | t }}
              </ng-template>
              <div class="hits-tab-content">
                <!-- Loading State -->
                <div *ngIf="isLoadingHotListDetails" class="hits-loading-state">
                  <mat-spinner color="accent" diameter="48"></mat-spinner>
                  <p class="loading-message">{{ 'alpr.hotlist.loadingDetails' | t }}</p>
                </div>

                <!-- Empty State -->
                <div *ngIf="!selectedPlate.hotlistInfo && !hasHits()" class="hits-empty-state">
                  <mat-icon class="empty-icon">info</mat-icon>
                  <p class="empty-message">{{ 'alpr.hotlist.noDetailsFound' | t }}</p>
                </div>

                <!-- Hotlist -->
                <div class="hits-container">
                  <!-- Hits Information from Plate Object -->
                  <div *ngIf="hasHits()" class="hits-section">
                    <div class="section-container">
                      <div class="section-header">
                        <h3>{{ 'alpr.hotlist.details.title' | t }}</h3>
                      </div>
                      <div class="section-content">
                        <div *ngFor="let hit of hits">
                          <div class="detail-row">
                            <div class="detail-cell">
                              <span class="detail-label">{{ 'alpr.hotlist.hotlistEntry' | t }}</span>
                              <span class="detail-value">{{ getHotlistTitle(hit) }}</span>
                            </div>
                            <div class="detail-cell">
                              <span class="detail-label">{{ 'alpr.hotlist.details.category' | t }}</span>
                              <span class="detail-value">{{ hit?.reason || '—' }}</span>
                            </div>
                            <div class="detail-cell" *ngIf="hit?.hotlist_id">
                              <span class="detail-label">{{ 'alpr.hotlist.details.priority' | t }}</span>
                              <span class="detail-value">{{ hit.hotlist_id }}</span>
                            </div>
                          </div>
                          <div class="detail-row" *ngIf="hit?.comment">
                            <div class="detail-cell">
                              <span class="detail-label">{{ 'alpr.hotlist.details.notes' | t }}</span>
                              <span class="detail-value">{{ hit.comment }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Vehicle Details from Plate Object -->
                    <div class="section-container" *ngIf="hasVehicleDetails()">
                      <div class="section-header">
                        <h3>{{ 'alpr.hotlist.vehicleDetails.title' | t }}</h3>
                      </div>
                      <div class="section-content">
                        <div class="detail-row">
                          <div class="detail-cell">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.plate' | t }}</span>
                            <span class="detail-value">{{ selectedPlate?.plate || '—' }}</span>
                          </div>
                          <div class="detail-cell">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.plateState' | t }}</span>
                            <span class="detail-value">{{ getPlateState() || '—' }}</span>
                          </div>
                          <div class="detail-cell" *ngIf="getVehicleYear()">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.year' | t }}</span>
                            <span class="detail-value">{{ getVehicleYear() }}</span>
                          </div>
                          <div class="detail-cell" *ngIf="getVehicleBody()">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.type' | t }}</span>
                            <span class="detail-value">{{ getVehicleBody() }}</span>
                          </div>
                        </div>
                        <div class="detail-row">
                          <div class="detail-cell" *ngIf="getVehicleColor()">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicle.color' | t }}</span>
                            <span class="detail-value">{{ getVehicleColor() | colorNameTranslation }}</span>
                          </div>
                          <div class="detail-cell" *ngIf="getVehicleMake()">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicle.make' | t }}</span>
                            <span class="detail-value">{{ getVehicleMake() }}</span>
                          </div>
                          <div class="detail-cell" *ngIf="getVehicleModel()">
                            <span class="detail-label">{{ 'alpr.hotlist.vehicle.model' | t }}</span>
                            <span class="detail-value">{{ getVehicleModel() }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div *ngIf="selectedPlate.hotlistInfo" class="hotlist-entry">
                  <div class="entry-header">
                    <h3>{{ selectedPlate.hotlistInfo?.listName || ('alpr.hotlist.hotlistEntry' | t) }}</h3>
                  </div>

                  <!-- Hotlist Details -->
                  <div class="section-container">
                    <div class="section-header">
                      <h3>{{ 'alpr.hotlist.details.title' | t }}</h3>
                    </div>
                    <div class="section-content">
                      <div class="detail-row">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.details.category' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.offenseCategoryName || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.details.priority' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.offensePriorityText || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.details.registration' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.registrationState || '—' }}</span>
                        </div>
                      </div>
                      <div class="detail-row" *ngIf="selectedPlate.hotlistInfo?.note">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.details.notes' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.note || '—' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Vehicle Details -->
                  <div class="section-container">
                    <div class="section-header">
                      <h3>{{ 'alpr.hotlist.vehicleDetails.title' | t }}</h3>
                    </div>
                    <div class="section-content">
                      <div class="detail-row">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.plate' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.licensePlate || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.plateState' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.registrationState || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.vin' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.vin || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.year' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.year || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleDetails.type' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.vehicleType || '—' }}</span>
                        </div>
                      </div>
                      <div class="detail-row">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicle.color' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.color || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicle.make' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.make || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicle.model' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.model || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicle.tabExpiration' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.carInfo?.tabExpirationDate || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicle.registrationExpiration' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.recordExpiration || '—' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Vehicle Owner Details -->
                  <div class="section-container">
                    <div class="section-header">
                      <h3>{{ 'alpr.hotlist.vehicleOwner.title' | t }}</h3>
                    </div>
                    <div class="section-content">
                      <div class="detail-row">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'common.firstName' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.firstName || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'common.lastName' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.lastName || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.addressLine1' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.address1 || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.addressLine2' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.address2 || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'common.city' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.city || '—' }}</span>
                        </div>
                      </div>
                      <div class="detail-row">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'common.state' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.state || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.zip' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.zip || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'common.gender' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.gender || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.weight' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.weight || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.height' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.height || '—' }}</span>
                        </div>
                      </div>
                      <div class="detail-row">
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.eyeColor' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.eyes || '—' }}</span>
                        </div>
                        <div class="detail-cell">
                          <span class="detail-label">{{ 'alpr.hotlist.vehicleOwner.dateOfBirth' | t }}</span>
                          <span class="detail-value">{{ selectedPlate.hotlistInfo?.ownerInfo?.birthdate || '—' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </mat-tab>
          </mat-tab-group>

          <div class="dropdown-tab">
            <mat-form-field appearance="outline" class="view-options">
              <mat-select
                [placeholder]="'alpr.selectTime' | t"
                [value]="defaultHistoricalSearchTimeOption"
                (selectionChange)="onHistoricalTimeChanged($event)"
              >
                <mat-option
                  *ngFor="let option of historicalSearchTimeOptions"
                  [value]="option.value"
                >
                  {{ option.label | t }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div
            class="resizeable-handle"
            (mousedown)="onResizeStart($event)"
            (touchstart)="onResizeStart($event)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</div>
